import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const WatchingVideos: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/help')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('help.backToHelp', 'Back to Help Center')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          For Viewers
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Watching Videos and Clips
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Master the IndeeClips video player with playback controls, quality settings, keyboard shortcuts, and tips for the best viewing experience.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Starting a Video
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click any video thumbnail to start playback</li>
          <li>Video opens in full player with recommended videos on the right (desktop) or below (mobile)</li>
          <li>Playback starts automatically</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Player Controls
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Play/Pause:</strong> Click video or press spacebar</li>
          <li><strong>Volume:</strong> Hover over volume icon and drag slider (or press up/down arrows)</li>
          <li><strong>Seek:</strong> Click progress bar or use left/right arrow keys</li>
          <li><strong>Fullscreen:</strong> Click fullscreen icon or press F</li>
          <li><strong>Settings:</strong> Gear icon for quality, speed, and captions</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Quality Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Click Settings (gear icon) → Quality:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Auto (default):</strong> Adjusts quality based on internet speed</li>
          <li><strong>1080p:</strong> Full HD (requires fast connection)</li>
          <li><strong>720p:</strong> HD (good balance of quality and data usage)</li>
          <li><strong>480p:</strong> SD (works on slower connections)</li>
          <li><strong>360p:</strong> Low quality (3G networks)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Playback Speed
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Settings → Playback Speed:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>0.25x, 0.5x, 0.75x (slow down for tutorials)</li>
          <li>1x (normal speed)</li>
          <li>1.25x, 1.5x, 1.75x, 2x (speed up for lectures or long content)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Keyboard Shortcuts (Desktop)
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Space:</strong> Play/Pause</li>
          <li><strong>K:</strong> Play/Pause (alternative)</li>
          <li><strong>J:</strong> Rewind 10 seconds</li>
          <li><strong>L:</strong> Fast forward 10 seconds</li>
          <li><strong>M:</strong> Mute/Unmute</li>
          <li><strong>F:</strong> Fullscreen</li>
          <li><strong>Arrow Up/Down:</strong> Volume control</li>
          <li><strong>Arrow Left/Right:</strong> Skip 5 seconds</li>
          <li><strong>0-9:</strong> Jump to percentage (5 = 50% through video)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Mobile Gestures
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Double-tap left/right:</strong> Skip backward/forward 10 seconds</li>
          <li><strong>Swipe up/down (right side):</strong> Volume control</li>
          <li><strong>Swipe up/down (left side):</strong> Brightness control</li>
          <li><strong>Pinch:</strong> Zoom video to fill screen</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Watching Clips
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Short-form vertical videos (IndeeClips Clips):
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Navigate to Clips section from sidebar</li>
          <li>Videos play in vertical format (like Instagram Reels/YouTube Shorts)</li>
          <li>Swipe up to see next clip, swipe down for previous</li>
          <li>Tap to pause, double-tap to like</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Autoplay
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          When a video ends, the next suggested video plays automatically (desktop only).
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Turn off:</strong> Settings → Playback → Disable Autoplay</li>
          <li><strong>Cancel next video:</strong> Click Cancel button before countdown ends</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Picture-in-Picture Mode
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Watch while browsing other apps or websites:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Desktop:</strong> Click PiP icon in player controls</li>
          <li><strong>Mobile:</strong> Swipe down while watching (video shrinks to corner)</li>
          <li><strong>Close PiP:</strong> Click X on floating player</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Theater and Fullscreen Modes
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Theater mode:</strong> Expands player while keeping sidebar visible</li>
          <li><strong>Fullscreen:</strong> Video fills entire screen (press Esc to exit)</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Playback Issues?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            If videos won't play or buffer constantly, contact{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default WatchingVideos;
